import classNames from 'classnames';
import Colors from '../../../global/colors';
import { bowser } from '../../../global/browser-support';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;

import styles from './progress.module.scss';

import { CSSProperties } from 'react';

export type ProgressGradient = {
  color: string;
  percent: string;
};

export type ProgressProps = {
  svgClassName?: string;
  initialAnimation?: boolean;
  percentage: number;
  background?: boolean;
  progressGradient?: {
    angle: number;
    colors: Array<ProgressGradient>;
  };
  progressColor?: string;
  progressBackground?: {
    stroke: string;
    opacity: number;
  };
  strokeWidth?: number;
  children?: React.ReactNode;
  coverChildren?: boolean;
  onAnimationComplete?: () => void;
  animationDurationSeconds?: number;
};

type State = {
  percentage: number;
};

class Progress extends React.Component<ProgressProps, State> {
  uniqueId: string;
  initialTimeout?: NodeJS.Timeout;
  animationTimeout?: NodeJS.Timeout;
  requestAnimationFrame?: number;

  constructor(props: ProgressProps) {
    super(props);

    this.uniqueId = Math.random().toString(36).substring(7);

    this.state = {
      percentage: props.initialAnimation ? 0 : props.percentage,
    };
  }

  static defaultProps = {
    strokeWidth: 10,
    animationDurationSeconds: 2,

    // @ts-ignore causes error in Zeus though not Storyboook
    initialAnimation: !bowser.msie,
  };

  componentDidMount() {
    if (this.props.initialAnimation) {
      this.initialTimeout = setTimeout(() => {
        this.requestAnimationFrame = window.requestAnimationFrame(() => {
          this.setState({
            percentage: this.props.percentage,
          });
          this.fireTransitionEnd();
        });
      }, 0);
    }
  }

  fireTransitionEnd() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    this.animationTimeout = setTimeout(
      () => {
        if (this.props.onAnimationComplete) {
          this.props.onAnimationComplete();
        }
        this.animationTimeout = undefined;
      },
      (this.props.animationDurationSeconds || 0) * 1000,
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps: ProgressProps) {
    this.setState({
      percentage: nextProps.percentage,
    });
    this.fireTransitionEnd();
  }

  componentWillUnmount() {
    clearTimeout(this.initialTimeout);
    clearTimeout(this.animationTimeout);
    if (this.requestAnimationFrame) {
      window.cancelAnimationFrame(this.requestAnimationFrame);
    }
  }

  getPathDescription(strokeWidth: number) {
    const radius = this.getPathRadius(strokeWidth);
    return `
      M 50,50 m 0,-${radius}
      a ${radius},${radius} 0 1 1 0,${2 * radius}
      a ${radius},${radius} 0 1 1 0,-${2 * radius}
    `;
  }

  getProgressStyle(strokeWidth: number, progressColor?: string) {
    const diameter = Math.PI * 2 * this.getPathRadius(strokeWidth);
    const truncatedPercentage = Math.min(
      Math.max(this.state.percentage, MIN_PERCENTAGE),
      MAX_PERCENTAGE,
    );
    return {
      strokeDasharray: `${diameter}px ${diameter}px`,
      strokeDashoffset: `${((100 - truncatedPercentage) / 100) * diameter}px`,
      stroke: progressColor || `url(${location.href}#${this.uniqueId})`,
      strokeLinecap: 'round' as CSSProperties['strokeLinecap'],
      transition: `stroke-dashoffset ${
        this.props.animationDurationSeconds || 0
      }s ease 0s`,
    };
  }

  getPathRadius(strokeWidth: number) {
    // the radius of the path is defined to be in the middle, so in order for the path to
    // fit perfectly inside the viewBox, need to subtract half the strokeWidth
    return 50 - strokeWidth / 2;
  }

  render() {
    const {
      strokeWidth = 10,
      progressGradient = {
        angle: 45,
        colors: [
          {
            color:
              this.props.percentage === 100
                ? Colors().green300
                : Colors().red300,
            percent: '0%',
          },
          {
            color:
              this.props.percentage === 100
                ? Colors().green300
                : Colors().red300,
            percent: '100%',
          },
        ],
      },
      progressBackground,
      progressColor,
      background,
      percentage,
      children,
      coverChildren,
      svgClassName,
    } = this.props;
    const pathDescription = this.getPathDescription(strokeWidth);

    return (
      <div
        className={classNames(
          styles.wrapper,
          coverChildren && styles['wrapper--cover-children'],
        )}
      >
        <svg
          className={svgClassName ? styles[svgClassName] : ''}
          viewBox='0 0 100 100'
        >
          <defs>
            <linearGradient
              id={this.uniqueId}
              gradientTransform={`rotate(${progressGradient.angle})`}
            >
              {progressGradient.colors.map((e: ProgressGradient, i: number) => (
                <stop key={i} offset={e.percent} stopColor={e.color} />
              ))}
            </linearGradient>
          </defs>

          {background && (
            <circle
              style={{ fill: Colors().primary700 }}
              cx={50}
              cy={50}
              r={40}
            />
          )}

          <path
            d={pathDescription}
            strokeWidth={strokeWidth}
            fillOpacity={0}
            style={
              progressBackground || {
                stroke: Colors().primary200,
              }
            }
          />

          {(percentage || percentage === 0) && (
            <path
              data-hook='progress-svg'
              d={pathDescription}
              strokeWidth={strokeWidth}
              fillOpacity={0}
              style={this.getProgressStyle(strokeWidth, progressColor)}
            />
          )}
        </svg>

        <div
          className={classNames(
            styles.children,
            coverChildren && styles['children--cover-children'],
          )}
        >
          {children}
        </div>
      </div>
    );
  }
}

export default Progress;
